import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Building2, Users, FileText, Wallet, Receipt, Wrench,
  Calculator, FileSignature, ShieldCheck, Menu, X, LogOut, UploadCloud, UserCircle, Moon, Sun, HardDrive, BarChart3, CalendarDays, ScrollText,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const NAV = [
  { to: "/dashboard", label: "Panel", icon: LayoutDashboard },
  { to: "/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/inquilinos", label: "Inquilinos", icon: Users },
  { to: "/contratos", label: "Contratos", icon: FileText },
  { to: "/pagos", label: "Pagos", icon: Wallet },
  { to: "/gastos", label: "Gastos", icon: Receipt },
  { to: "/incidencias", label: "Incidencias", icon: Wrench },
  { to: "/irpf", label: "Declaración Renta", icon: Calculator },
  { to: "/estadisticas", label: "Estadísticas", icon: BarChart3 },
  { to: "/plantilla", label: "Plantillas", icon: FileSignature },
  { to: "/importar", label: "Importar Excel", icon: UploadCloud },
  { to: "/backups", label: "Copias Seguridad", icon: HardDrive },
  { to: "/cuenta", label: "Cuenta", icon: UserCircle },
  { to: "/pisos", label: "Pisos", icon: Building2 },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { properties, selectedId, selectProperty } = useProperty();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <button
        type="button"
        onClick={() => { navigate("/resumen"); setOpen(false); }}
        className="flex items-center gap-2.5 px-5 h-16 border-b border-border text-left hover:bg-secondary/40 transition-colors"
        title="Resumen general de todos los pisos"
        data-testid="brand-home-button"
      >
        <div className="h-8 w-8 bg-primary rounded-md flex items-center justify-center">
          <Building2 className="h-4.5 w-4.5 text-primary-foreground" strokeWidth={2} />
        </div>
        <span className="font-display font-semibold tracking-tight">RentaPiso</span>
      </button>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            data-testid={`nav-${item.to.replace("/", "")}`}
          >
            <item.icon className="h-4.5 w-4.5" strokeWidth={1.75} />
            {item.label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <>
            <NavLink
              to="/admin"
              onClick={() => setOpen(false)}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-admin"
            >
              <ShieldCheck className="h-4.5 w-4.5" strokeWidth={1.75} />
              Administración
            </NavLink>
            <NavLink
              to="/auditoria"
              onClick={() => setOpen(false)}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-auditoria"
            >
              <ScrollText className="h-4.5 w-4.5" strokeWidth={1.75} />
              Auditoría
            </NavLink>
          </>
        )}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-2">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
              {user?.name?.[0] || "U"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.role === "admin" ? "Administrador" : "Propietario"}
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="text-muted-foreground hover:text-destructive transition-colors p-1.5"
            title="Cerrar sesión"
          >
            <LogOut className="h-4.5 w-4.5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-sidebar border-r border-border fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-sidebar border-r border-border">
            <button
              className="absolute top-4 right-4 text-muted-foreground"
              onClick={() => setOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card flex items-center gap-4 px-4 lg:px-8 sticky top-0 z-20">
          <button
            className="lg:hidden text-foreground"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2 flex-1">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            {properties.length > 0 ? (
              <Select value={selectedId} onValueChange={selectProperty}>
                <SelectTrigger
                  className="w-auto min-w-[200px] border-0 shadow-none font-display font-medium text-base focus:ring-0 px-1"
                  data-testid="property-selector"
                >
                  <SelectValue placeholder="Selecciona un piso" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`property-option-${p.id}`}>
                      {p.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <button
                onClick={() => navigate("/pisos")}
                className="text-sm text-primary font-medium"
              >
                + Crea tu primer piso
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={toggleTheme}
            className="h-9 w-9 rounded-md border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary transition-colors flex items-center justify-center"
            title={theme === "dark" ? "Cambiar a tema claro" : "Cambiar a tema oscuro"}
            data-testid="theme-toggle-button"
          >
            {theme === "dark" ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
          </button>
        </header>

        <main className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
